import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import AdminLogin from './admin/AdminLogin';
import AdminDashboard from './admin/AdminDashboard';
import Businesses from './admin/Businesses';
import BusinessLanding from './business/BusinessLanding';
import BusinessLogin from './business/BusinessLogin';
import BusinessDashboard from './business/BusinessDashboard';
import BusinessInterestForm from './business/BusinessInterestForm';
import FacilitySelection from './business/FacilitySelection';
import MemberLanding from './member/MemberLanding';
import MemberLogin from './member/MemberLogin';
import NewMemberRegister from './member/MemberRegister';
import { NewSubscription } from './member/SubscriptionRegisterFlow';
import MemberDashboard from './member/MemberDashboard';
import { PlanSelection } from './member/PlanSelection';
import { GuestPage } from './guest/GuestPage';
import { GuestFacilityDetails } from './guest/GuestFacilityDetails';
import { GuestBookingSuccess } from './guest/GuestBookingSuccess';
import StaffLogin from './staff/StaffLogin';
import StaffDashboard from './staff/StaffDashboard';
import { EmailVerification } from './member/EmailVerification';
import CompleteSetup from './member/CompleteSetup';
import DocumentPreviewPage from './pages/DocumentPreviewPage';
import PPUFacilitiesList from './member/PPU_facilities_list';

// Create a client
const queryClient = new QueryClient();

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Router>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Navigate to="/member" replace />} />
            <Route path="/member" element={<MemberLanding />} />
            <Route path="/business" element={<BusinessLanding />} />
            <Route path="/guest" element={<GuestPage />} />
            
            {/* Admin Routes */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/dashboard" element={<AdminDashboard />} />
            <Route path="/admin/businesses" element={<Businesses />} />
            
            {/* Document Preview */}
            <Route path="/documents/preview" element={<DocumentPreviewPage />} />
            
            {/* Business Routes */}
            <Route path="/business/interest-form" element={<BusinessInterestForm />} />
            <Route path="/business/login" element={<BusinessLogin />} />
            <Route
              path="/business/facility-selection"
              element={
                <ProtectedRoute allowedRoles={['business']}>
                  <FacilitySelection />
                </ProtectedRoute>
              }
            />
            <Route
              path="/business/dashboard/*"
              element={
                <ProtectedRoute allowedRoles={['business']}>
                  <BusinessDashboard />
                </ProtectedRoute>
              }
            />
            
            {/* Staff Routes */}
            <Route path="/staff/login" element={<StaffLogin />} />
            <Route
              path="/staff/dashboard/*"
              element={
                <ProtectedRoute allowedRoles={['staff']}>
                  <StaffDashboard />
                </ProtectedRoute>
              }
            />
            
            {/* Member Routes */}
            <Route path="/member/login" element={<MemberLogin />} />
            <Route path="/member/register" element={<NewMemberRegister />} />
            <Route path="/member/complete-setup" element={<CompleteSetup />} />
            <Route path="/member/verify-email" element={<EmailVerification />} />
            <Route path="/member/plans" element={<PlanSelection />} />
            <Route path="/member/new-subscription" element={<NewSubscription />} />
            <Route path="/member/ppu-facilities" element={<PPUFacilitiesList />} />
            <Route
              path="/member/dashboard/*"
              element={
                <ProtectedRoute allowedRoles={['member']}>
                  <MemberDashboard />
                </ProtectedRoute>
              }
            />

            
            {/* Guest Routes */}
            <Route path="/guest/businesses/:businessId/facilities/:facilityId" element={<GuestFacilityDetails />} />
            <Route path="/guest/bookings/:bookingId/success" element={<GuestBookingSuccess />} />
            
            {/* Catch all route */}
            <Route path="*" element={<Navigate to="/member" replace />} />
          </Routes>
        </Router>
      </AuthProvider>
    </QueryClientProvider>
  );
};

export default App;
